import { Button, Text, VSpacer } from '@/components/DesignSystem';
import { Avatar } from '@/components/DesignSystem/Avatar/Avatar';
import { LoyaltySkuBadge } from '@/components/shared/LoyaltySkuBadge';
import {
  SubwalletProductsBottomSheet,
} from '@/components/shared/SubwalletBalance/SubwalletProductsBottomSheet';
import { AppConfig } from '@/constants/AppConfig';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Collapse, Dialog, DialogContent, DialogTitle, Stack, useTheme } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface RedemptionSku {
  id: string,
  sku: string,
  description?: string,
}

interface Subwallet {
  id: string,
  name: string,
  logo?: string,
  pointsRemaining: number,
  description?: string,
  hasPromotions: boolean,
  redemptionSkus: RedemptionSku[],
}

interface ReservedPointsModalProps {
  open: boolean,
  onClose: () => void,
  bookingId: string,
  subwallets: Subwallet[],
}

const SubwalletCard = ({ subwallet }: { subwallet: Subwallet }) => {
  const [expanded, setExpanded] = useState(false);
  const [showRedeemableProducts, setShowRedeemableProducts] = useState(false);
  const theme = useTheme();
  const { t } = useTranslation();

  const getLogo = (subwallet: Subwallet) => {
    return subwallet.logo;
  };

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '8px',
        overflow: 'hidden',
        mb: 2,
      }}
    >
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="space-between"
        onClick={() => setExpanded(!expanded)}
        sx={{
          p: 2,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }}
      >
        <Stack alignItems="center" direction="row" spacing={2} sx={{ flex: 1, minWidth: 0, mr: 2 }}>
          <Avatar
            alt={`${subwallet.name} logo`}
            defaultIcon="manufacturer"
            src={subwallet && getLogo(subwallet)
              ? `${AppConfig.staticImageHost}/${getLogo(subwallet)}`
              : undefined
            }
            sx={{ height: '48px', width: '48px', flexShrink: 0 }}
          />
          <Stack sx={{ minWidth: 0, flex: 1 }}>
            <Text category="overline">
              {t($ => $.bookings.reservedPoints.label)}
            </Text>
            <Text category="body-xlarge" sx={{ wordBreak: 'break-word' }}>
              {subwallet.name}
            </Text>
          </Stack>
        </Stack>
        <Stack alignItems="flex-end" direction="row" spacing={2} sx={{ flexShrink: 0 }}>
          <Box
            sx={{
              backgroundColor: '#FFE312',
              borderRadius: '4px',
              px: 1,
              py: 0.5,
              whiteSpace: 'nowrap',
            }}
          >
            <Text category="body-medium">
              {t($ => $.common.booking.pointsRemaining,
                { count: subwallet.pointsRemaining })}
            </Text>
          </Box>
          {expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </Stack>
      </Stack>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ p: 2, pt: 0 }}>
          {subwallet.description && (
            <>
              <Text category="body-medium">
                {subwallet.description}
              </Text>
              <VSpacer size="4" />
            </>
          )}
          {subwallet.hasPromotions && (
            <>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowRedeemableProducts(true);
                }}
                size="medium"
                square
                testID={`view-redeemable-products-${subwallet.id}`}
                variant="contained"
              >
                {t($ => $.bookings.reservedPoints.viewRedeemable)}
              </Button>
              <VSpacer size="4" />
            </>
          )}
          {subwallet.redemptionSkus.length > 0 && (
            <Stack direction="row" flexWrap="wrap" sx={{ gap: 1.5 }} >
              {subwallet.redemptionSkus.map((sku) => (
                <LoyaltySkuBadge key={sku.id} sku={sku.sku} />
              ))}
            </Stack>
          )}
        </Box>
      </Collapse>
      {showRedeemableProducts && (
        <SubwalletProductsBottomSheet
          onClose={() => setShowRedeemableProducts(false)}
          subwalletId={subwallet.id}
        />
      )}
    </Box>
  );
};

export const ReservedPointsModal = ({
  open,
  onClose,
  bookingId,
  subwallets,
}: ReservedPointsModalProps) => {
  const { t } = useTranslation();

  // Filter subwallets to only show those with points remaining > 0
  const filteredSubwallets = subwallets.filter((sw) => sw.pointsRemaining > 0);

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      onClose={onClose}
      open={open}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '12px',
          maxHeight: '90vh',
          width: '560px',
        },
      }}
    >
      <DialogTitle
        sx={{
          borderRadius: '28px 28px 0 0',
          margin: '20px 24px 12px 24px',
        }}
      >
        <Stack alignItems="center" direction="row" justifyContent="space-between">
          <Text category="headline-small">
            {t($ => $.bookings.reservedPoints.title)}
          </Text>
          <Stack alignItems="center" direction="row" spacing={2}>
            <Text category="label-medium">
              {t($ => $.orders.details.booking, { bookingId })}
            </Text>
          </Stack>
        </Stack>
      </DialogTitle>
      <DialogContent
        onClick={(e) => e.stopPropagation()}
        sx={{ pt: 3 }}
      >
        {filteredSubwallets.length === 0 ? (
          <Stack alignItems="center" py={4}>
            <Text category="body-medium" color="secondary">
              {t($ => $.bookings.reservedPoints.emptyState)}
            </Text>
          </Stack>
        ) : (
          <Stack>
            {filteredSubwallets.map((subwallet) => (
              <SubwalletCard key={subwallet.id} subwallet={subwallet} />
            ))}
          </Stack>
        )}
        <VSpacer size="4" />
        <Stack alignItems="flex-end">
          <Button
            onClick={onClose}
            size="large"
            testID="close-reserved-points-modal"
            variant="contained"
          >
            {t($ => $.common.actions.close)}
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
